import { AnimatedNumber } from '../../../../components/AnimatedNumber';
import { OMS_COLORS } from '../omsDashTheme';
import { formatNumber } from '../utils/formatNumber';
import CalcHint from './CalcHint';
import { DeltaChip } from './HealthScore';

/**
 * THE count display — the only way a task/project/engineer count may appear
 * on the dashboard ("no naked numbers" rule). Tabular numeral with its noun
 * and denominator spelled out ("84 of 115 tasks", "31 tasks open"), an
 * 11px muted microlabel above, and unit-named deltas
 * ("▼ 12 tasks vs last week"). No band colours, no gauges — those belong
 * exclusively to health scores, so the two can never be confused.
 */
export interface StatCountProps {
  /** Microlabel above the numeral (11px muted). '' skips it — only allowed
   *  when a visible label already sits beside the figure (e.g. pipeline rows). */
  label: string;
  value: number;
  /** The unit noun — "tasks", "projects", "engineers". Required: no naked numbers. */
  noun: string;
  /** Denominator → "84 of 115 tasks". */
  of?: number;
  /** Trailing qualifier → "31 tasks open", "5 tasks overdue". */
  qualifier?: string;
  /** Override the numeral (e.g. "94%") while keeping the noun phrase. */
  valueText?: string;
  /** Numeral ink; defaults to racing green. */
  ink?: string;
  /** Numeral size. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  align?: 'start' | 'end';
  /** Animate the numeral (count-up) — only for hero figures. */
  animated?: boolean;
  /** Pass to show "▼ N <noun> <deltaPhrase>"; omit to hide. */
  delta?: number | null;
  deltaPhrase?: string;
  /** CalcHint lines; parent needs `group relative` for the hover reveal. */
  calc?: string[];
  className?: string;
}

const VALUE_SIZE: Record<NonNullable<StatCountProps['size']>, string> = {
  sm: 'text-[13px]',
  md: 'text-lg',
  lg: 'text-2xl',
  xl: 'text-5xl',
};

export default function StatCount({
  label,
  value,
  noun,
  of,
  qualifier,
  valueText,
  ink = OMS_COLORS.tealDark,
  size = 'md',
  align = 'start',
  animated = false,
  delta,
  deltaPhrase = 'vs previous window',
  calc,
  className = '',
}: StatCountProps) {
  const nounPhrase = `${of !== undefined ? ` of ${formatNumber(of)}` : ''} ${noun}${qualifier ? ` ${qualifier}` : ''}`;
  return (
    <span className={`block min-w-0 ${align === 'end' ? 'text-right' : 'text-left'} ${className}`}>
      {label && (
        <span className="block text-[11px] font-semibold leading-tight" style={{ color: OMS_COLORS.caption }}>
          {label}
        </span>
      )}
      <span className={`block ${VALUE_SIZE[size]} font-extrabold leading-tight tabular-nums`} style={{ color: ink }}>
        {valueText ?? (animated ? <AnimatedNumber value={value} /> : formatNumber(value))}
        <span className="text-[11px] font-semibold" style={{ color: OMS_COLORS.caption }}>
          {nounPhrase}
        </span>
      </span>
      {delta !== undefined && (
        <span className="block">
          <DeltaChip delta={delta} phrase={`${noun} ${deltaPhrase}`} />
        </span>
      )}
      {calc && <CalcHint lines={calc} />}
    </span>
  );
}
